import { commandsStore } from "@/store/commandsStore";
import { headerBiddingStore } from "@/store/headerBiddingStore";
import { isAuctionModule, isPageDataCommand, isTriggerAuctionCommand, } from "@/ts/guards";
import type { AuctionControllerConfig, AuctionControllerModule, ModuleConfig, } from "@/ts/module";
import type { DisplaySlotConfig, Exactly, LotameData, Ortb2, PageDataCommand, SlotTargeting, TriggerAuctionCommand, VideoSlotConfig, } from "@/ts/types";
import { markTiming } from "@/utils/commercialDebug";
import { getModules } from "@/utils/modules";
export class AuctionController implements Exactly<AuctionControllerModule, AuctionController> {
    private config: AuctionControllerConfig | undefined;
    name = "AuctionController" as const;
    constructor(auctionConfig: ModuleConfig) {
        if ("defaultTimeout" in auctionConfig) {
            this.config = auctionConfig;
        }
    }
    async init(): Promise<void> {
        const { selectors } = commandsStore.getState();
        // Subscribe to Trigger Auction command
        commandsStore.subscribe(selectors.selectTriggerAuctionCommand, (selectedCmd) => handleAuctionCmd(this.run, selectedCmd));
        // Subscribe to Trigger Page data command
        commandsStore.subscribe(selectors.selectPageDataCommand, (selectedState) => handlePageDataCmd(selectedState));
    }
    async run(slotConfigs: (DisplaySlotConfig | VideoSlotConfig)[], callback: (slotTargeting: Record<string, SlotTargeting> | undefined) => void, timeout = this.config?.defaultTimeout || 2000) {
        // biome-ignore lint/suspicious/noConsole: <explanation>
        // biome-ignore lint/suspicious/noConsoleLog: <explanation>
        console.log("8888 HYDRA auction controller slotConfigs", slotConfigs);
        const { auctions, actions, selectors, vendorNames } = headerBiddingStore.getState();
        const auctionId = Object.keys(auctions).length;
        actions.addAuctionToStore(auctionId, vendorNames);
        const vendorModules = await getModules(vendorNames);
        const vendors = vendorModules.filter(isAuctionModule);
        headerBiddingStore.subscribe(selectors.allVendorAuctionsDone(auctionId), (allDone) => allDone && handleAuctionCallback(auctionId, callback));
        if (vendors.length === 0) {
            handleAuctionCallback(auctionId, callback);
            return;
        }
        const args = [auctionId, slotConfigs, timeout] as const;
        for (const vendor of vendors) {
            vendor.run(...args);
            const slots = slotConfigs.map((slot) => slot.slotId);
            markTiming(`${vendor.name} auction started for : 
        auctionId: ${auctionId} 
        slots: ${slots} 
        timeout: ${args[2]} `);
        }
        setTimeout(() => {
            const { auctions } = headerBiddingStore.getState();
            const isAuctionComplete = auctions[auctionId].done;
            // dont call callback a second time if the happy path already called it
            if (!isAuctionComplete) {
                handleAuctionCallback(auctionId, callback);
            }
        }, timeout);
    }
}
const handleAuctionCallback = (auctionId: number, callback: (slotTargeting: Record<string, SlotTargeting> | undefined) => void) => {
    const { actions, selectors } = headerBiddingStore.getState();
    actions.setAuctionDone(auctionId);
    const slotTargeting = selectors.getSlotTargeting(auctionId);
    callback(slotTargeting);
    markTiming(`auction callback called for auctionId: ${auctionId}`);
};
const handleAuctionCmd = (run: (slotConfigs: (DisplaySlotConfig | VideoSlotConfig)[], callback: () => void, timeout: number) => void, selectedCmd?: TriggerAuctionCommand) => {
    if (selectedCmd && isTriggerAuctionCommand(selectedCmd)) {
        const { slots, timeout, callback } = selectedCmd;
        run(slots, callback, timeout);
    }
};
const handlePageDataCmd = (selectedCmd?: PageDataCommand) => {
    if (selectedCmd && isPageDataCommand(selectedCmd)) {
        const { lotame, mantis, misc: pageMisc } = selectedCmd.data;
        const ortb2: Ortb2 = {
            user: getUserData(lotame, mantis),
            site: getSiteData(mantis),
        };
        const auctionData = {
            ortb2,
            pageMisc,
        };
        const { actions } = headerBiddingStore.getState();
        actions.addAuctionData(auctionData);
    }
};
/**
 * Function that returns ortb2 data that can be readily plugged into setConfig of pbjs object
 * Refer the below links that suggests the below structure for ortb2 requirement.
 * https://docs.prebid.org/features/firstPartyData.html#segments-and-taxonomy
 * https://bitbucket.org/trinitymirror-ondemand/withnail/src/19e43d67c4305d893857d3447c5bb5ae3dc3c9da/src/tags/js/prebid.js#lines-198
 * https://bitbucket.org/trinitymirror-ondemand/withnail/src/19e43d67c4305d893857d3447c5bb5ae3dc3c9da/src/tags/js/prebid.js#lines-415
 */
const getUserData = (lotameData?: LotameData[], mantisKeywords?: string) => {
    const lotame = {
        name: "lotame.com",
        ext: { segtax: 4 },
        segment: lotameData,
    };
    const keywords = mantisKeywords;
    return lotame || keywords ? { data: [lotame], keywords } : undefined;
};
const getSiteData = (mantisKeywords?: string) => {
    return mantisKeywords ? { keywords: mantisKeywords } : undefined;
};
